import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";
import {
  standingKey,
  toServerQuery,
  type EventPage,
  type EventRow,
  type StandingFilters,
} from "./vocab.ts";

/**
 * The reads for the Events explorer (task 11.12; design 12.2 Events row). Both operations are role
 * `viewer`, taken straight from `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/events?from=…&to=…&<standing filters>&order&limit` → {@link EventPage}: one page of
 *   redacted {@link EventRow}s for the HISTORICAL / scrolled-back view, with the sample-rate-aware
 *   `weighted_count` and the `meta.data_quality` the screen reads to label sampling. Only the
 *   STANDING filters the contract accepts are sent (built by {@link toServerQuery}); the drill chips
 *   that the `/events` contract does not accept are applied client-side by the screen.
 * - `GET /api/v1/events/{id}` → {@link EventRow}: one event's full redacted detail (opened from a
 *   row).
 *
 * The LIST is a WINDOWED read (it takes `from`/`to`), so — exactly like the other windowed screens —
 * the window is resolved from the shared time-range and pausing freezes the query: live mode buckets
 * "now" to a 10 s grid to keep the query key stable between ticks; frozen (paused or custom) uses the
 * exact window and the key stops advancing (design 12.2, FR-13 AC2). While LIVE, the live tail (the
 * stream view's events ring) is the primary surface the screen shows; the paged read backs the
 * frozen/scrolled-back view and supplies the honest weighted count and data-quality for the window.
 *
 * Fetches go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} (the
 * shell shows "Waking the control plane"); a 404 for an unknown event id surfaces as `not_found`.
 */

/** The resolved window a list query ran for, so the screen can label the snapshot honestly. */
export interface EventsWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseEventsOptions {
  /** The standing filters to send to the server (chips are applied client-side, not here). */
  readonly standing: StandingFilters;
  /** Page size; the contract default is 100 (max 500). */
  readonly limit?: number;
  /** Newest-first (`desc`, default) or oldest-first (`asc`). */
  readonly order?: "desc" | "asc";
  /**
   * When false, the paged read does not run (e.g. the role is below viewer). Defaults to true. The
   * live tail is independent of this — it comes from the always-on stream driver.
   */
  readonly enabled?: boolean;
}

export interface UseEventsResult {
  readonly query: UseQueryResult<EventPage, ApiError>;
  /** The window the current query key was built from (what the page describes). */
  readonly window: EventsWindow;
}

export interface UseEventResult {
  readonly query: UseQueryResult<EventRow, ApiError>;
}

/**
 * Fetch one page of events for the current window and standing filters. The query key carries the
 * window AND a stable fragment of the standing filters ({@link standingKey}) so the page refetches
 * exactly when a server-affecting filter or the window changes — and NOT when only a client-side
 * chip changes (chips re-filter in memory). `staleTime` / `refetchInterval` match the other windowed
 * screens (live tolerates a little staleness and polls; frozen never goes stale and does not poll).
 * The first page only — `next_cursor` is left for a later pagination affordance, matching the
 * identity/posture/traces screens.
 */
export function useEvents(options: UseEventsOptions): UseEventsResult {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  const window: EventsWindow = { fromMs: resolved.fromMs, toMs: resolved.toMs };

  const order = options.order ?? "desc";
  const limit = options.limit ?? 100;
  const serverQuery = toServerQuery(options.standing);
  const enabled = options.enabled ?? true;

  const query = useQuery<EventPage, ApiError>({
    queryKey: [
      "events",
      window.fromMs,
      window.toMs,
      order,
      limit,
      standingKey(options.standing),
    ],
    queryFn: ({ signal }) =>
      apiFetch<EventPage>("/events", {
        query: {
          from: window.fromMs,
          to: window.toMs,
          order,
          limit,
          ...serverQuery,
        },
        signal,
      }),
    enabled,
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch one event's full redacted detail by id. An event is immutable once recorded, so the query is
 * keyed on the id alone and never goes stale — opening an event does not depend on the live/pause
 * state. `enabled` is false when there is no id, so no request goes out for an event that cannot
 * exist; a placeholder id keeps the key well-typed while disabled.
 */
export function useEvent(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseEventResult {
  const enabled = (options.enabled ?? true) && id !== undefined && id.length > 0;
  const eventId = id ?? "";

  const query = useQuery<EventRow, ApiError>({
    queryKey: ["event", eventId],
    queryFn: ({ signal }) =>
      apiFetch<EventRow>(`/events/${encodeURIComponent(eventId)}`, { signal }),
    enabled,
    staleTime: Infinity,
    refetchInterval: false,
  });

  return { query };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
